import { Dialog, DialogContent } from "./ui/dialog";
import { X, ShoppingBag, ExternalLink, Share2, Check, ArrowUpRight } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { genreLabel, bookShareUrl } from "../lib/api";
import { SocialShareButtons } from "./SocialShareButtons";

export const BookDetailDialog = ({ book, open, onClose }) => {
  const [copied, setCopied] = useState(false);
  if (!book) return null;

  const bookUrl = bookShareUrl(book.id);
  const handleShare = async () => {
    const text = `Découvrez "${book.title}" par ${book.author} chez Challenges Littéraires Éditions`;
    if (navigator.share) {
      try { await navigator.share({ title: book.title, text, url: bookUrl }); return; } catch (e) { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(bookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      window.prompt("Copiez le lien ci-dessous :", bookUrl);
    }
  };

  const orderMailto = `mailto:challengeslitteraires.editions@gmail.com?subject=${encodeURIComponent("Commande — " + book.title)}&body=${encodeURIComponent("Bonjour,\n\nJe souhaite commander l'ouvrage « " + book.title + " » de " + book.author + ".\n\nMerci de me communiquer les modalités d'achat.\n\nCordialement,")}`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid={`book-detail-${book.id}`}
        className="max-w-4xl max-h-[92vh] overflow-y-auto bg-[#FAF9F6] border border-[#D1CFC7] rounded-none p-0"
      >
        <button
          onClick={onClose}
          data-testid="close-book-detail"
          className="absolute top-4 right-4 z-10 bg-[#FAF9F6] border border-[#D1CFC7] hover:border-[#2C402E] p-2 transition-colors"
          aria-label="Fermer"
        >
          <X size={18} className="text-[#1A1A1A]" />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-0">
          {/* Cover */}
          <div className="md:col-span-5 bg-[#F4F1EA] flex items-center justify-center p-8 md:p-10">
            <img
              src={book.cover_url}
              alt={book.title}
              className="w-full max-w-[280px] shadow-2xl"
            />
          </div>

          {/* Details */}
          <div className="md:col-span-7 p-8 md:p-10 lg:p-12">
            {book.is_new && (
              <span className="inline-block bg-[#9E4735] text-[#FAF9F6] text-[10px] tracking-[0.2em] uppercase font-body px-3 py-1 mb-4">
                Nouveauté
              </span>
            )}
            <p className="text-[10px] tracking-[0.25em] uppercase font-body text-[#9E4735] mb-3">
              {genreLabel(book.genre)}
              {book.collection && (
                <span className="text-[#757575] normal-case tracking-normal"> · Collection {book.collection}</span>
              )}
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#1A1A1A] leading-tight font-light mb-3">
              {book.title}
            </h2>
            <p className="text-base font-body text-[#4A4A4A] mb-6">
              par <em>{book.author}</em>
            </p>

            <div className="flex flex-wrap gap-x-8 gap-y-2 text-xs font-body text-[#757575] mb-8 pb-6 border-b border-[#D1CFC7]">
              {book.price && (
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#9E4735]">Prix</p>
                  <p className="font-mono text-lg text-[#1A1A1A] mt-0.5">{book.price.toFixed(2)} €</p>
                </div>
              )}
              {book.publication_date && (
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#9E4735]">Parution</p>
                  <p className="text-base text-[#1A1A1A] mt-0.5">
                    {new Date(book.publication_date).toLocaleDateString("fr-FR", { year: "numeric", month: "long" })}
                  </p>
                </div>
              )}
              {book.pages && (
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#9E4735]">Pages</p>
                  <p className="text-base text-[#1A1A1A] mt-0.5">{book.pages}</p>
                </div>
              )}
              {book.isbn && (
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#9E4735]">ISBN</p>
                  <p className="font-mono text-sm text-[#1A1A1A] mt-0.5">{book.isbn}</p>
                </div>
              )}
              {book.formats && book.formats.length > 0 && (
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#9E4735]">Formats</p>
                  <p className="text-base text-[#1A1A1A] mt-0.5">
                    {book.formats.includes("papier_partenaires")
                      ? "Papier (disponible au Cameroun/Tchad)"
                      : book.formats.map((f) => f === "papier" ? "Papier" : "Ebook").join(" · ")}
                  </p>
                </div>
              )}
            </div>

            <p className="text-base font-body text-[#1A1A1A] leading-relaxed whitespace-pre-line mb-10">
              {book.description}
            </p>

            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              {book.amazon_url ? (
                <a
                  href={book.amazon_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`detail-buy-${book.id}`}
                  className="inline-flex items-center justify-center gap-2 bg-[#2C402E] text-[#FAF9F6] hover:bg-[#1F2D21] px-8 py-3 font-body tracking-wide transition-all"
                >
                  <ShoppingBag size={16} />
                  Acheter sur Amazon
                  <ExternalLink size={14} />
                </a>
              ) : (
                <a
                  href={orderMailto}
                  data-testid={`detail-order-${book.id}`}
                  className="inline-flex items-center justify-center gap-2 bg-[#2C402E] text-[#FAF9F6] hover:bg-[#1F2D21] px-8 py-3 font-body tracking-wide transition-all whitespace-nowrap"
                >
                  <ShoppingBag size={16} />
                  Commander auprès de CLED
                </a>
              )}
              <button
                onClick={handleShare}
                data-testid={`detail-share-${book.id}`}
                className="inline-flex items-center justify-center gap-2 border border-[#2C402E] text-[#2C402E] hover:bg-[#2C402E] hover:text-[#FAF9F6] px-6 py-3 font-body transition-all"
              >
                {copied ? <><Check size={16} /> Lien copié !</> : <><Share2 size={16} /> Partager</>}
              </button>
              <Link
                to={`/livres/${book.id}`}
                onClick={onClose}
                data-testid={`detail-page-${book.id}`}
                className="inline-flex items-center justify-center gap-2 text-[#1A1A1A] hover:text-[#2C402E] px-4 py-3 font-body text-sm transition-all"
              >
                Page dédiée <ArrowUpRight size={14} />
              </Link>
            </div>
            <p className="text-xs text-[#757575] font-body mt-4">
              Ou commander via la{" "}
              <a
                href="https://www.facebook.com/share/1BbuPaUifd/"
                target="_blank"
                rel="noreferrer"
                data-testid={`detail-fb-${book.id}`}
                className="text-[#2C402E] underline hover:text-[#9E4735] transition-colors"
              >
                page Facebook Pick & Read
              </a>
            </p>

            {/* Boutons de partage social rapide */}
            <div className="mt-6 pt-5 border-t border-[#D1CFC7]">
              <SocialShareButtons
                url={bookUrl}
                title={book.title}
                author={book.author}
                testId={`modal-share-${book.id}`}
              />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
